import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  Bell, Home, PenSquare, ShieldCheck, Calendar, Settings,
  LogOut, Menu, X, User,
} from "lucide-react";
import { useUser } from "../contexts/UserContext";
import { WordmarkShort } from "./Wordmark";
import { api } from "../lib/api";

const LEVEL_LABELS = {
  ceo: "CEO", direction: "Direction", dn: "Directeur National",
  dr: "Directeur Régional", consultant: "Consultant", president: "Président",
};

export default function Layout({ children }) {
  const { user, logout } = useUser();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Notifications polling : se re-démarre quand l'utilisateur change.
  // `api` est un import stable, donc on n'a besoin que de `user` dans les deps.
  useEffect(() => {
    if (!user) return;
    const fetchUnread = () =>
      api
        .get("/notifications?only_unread=true&limit=1")
        .then((r) => setUnread(r.data.unread_count || 0))
        .catch(() => {});
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => clearInterval(id);
  }, [user]);

  // Close avatar menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!user) return null;

  const canModerate = ["ceo", "direction", "dn", "dr"].includes(user.hierarchy_level);
  const canAdmin = ["ceo", "direction"].includes(user.hierarchy_level);

  const nav = [
    { to: "/", label: "Feed", icon: Home, testid: "nav-feed" },
    { to: "/compose", label: "Publier", icon: PenSquare, testid: "nav-compose" },
    { to: "/events", label: "Événements", icon: Calendar, testid: "nav-events" },
    ...(canModerate ? [{ to: "/moderation", label: "Modération", icon: ShieldCheck, testid: "nav-moderation" }] : []),
    ...(canAdmin ? [{ to: "/admin/categories", label: "Admin", icon: Settings, testid: "nav-admin" }] : []),
  ];

  const initials = `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase();

  return (
    <div className="min-h-screen" style={{ background: "#FAFAFA" }}>
      <header
        className="sticky top-0 z-50 backdrop-blur-md"
        style={{
          background: "rgba(255,255,255,0.92)",
          boxShadow: "0 1px 0 #E5E5E5",
        }}
        data-testid="app-header"
      >
        <div className="max-w-[1024px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="header-wordmark-link">
            <WordmarkShort />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "bg-orange-50" : "hover:bg-gray-50"
                  }`
                }
                style={({ isActive }) => ({ color: isActive ? "#F77F00" : "#1A1A1A" })}
              >
                <n.icon size={16} />
                <span>{n.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/notifications")}
              className="relative p-2 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="header-notifications-button"
              title="Notifications"
            >
              <Bell size={18} color="#1A1A1A" />
              {unread > 0 && (
                <span
                  data-testid="header-notif-badge"
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold text-white flex items-center justify-center px-1"
                  style={{ background: "#F77F00" }}
                >
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>

            {/* Avatar dropdown */}
            <div className="relative hidden sm:block" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-3 pl-3 ml-1 py-1 pr-1 rounded-full border-l hover:bg-gray-50 transition-colors"
                style={{ borderColor: "#E5E5E5" }}
                data-testid="avatar-trigger"
              >
                <div className="text-right">
                  <div className="text-sm font-semibold" data-testid="header-user-name">
                    {user.first_name} {user.last_name}
                  </div>
                  <div className="text-xs" style={{ color: "#666" }} data-testid="header-user-level">
                    {LEVEL_LABELS[user.hierarchy_level]}
                  </div>
                </div>
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white"
                    style={{ background: "#F77F00" }}
                  >
                    {initials}
                  </div>
                )}
              </button>

              {menuOpen && (
                <div
                  className="absolute right-0 mt-2 w-56 bg-white rounded-xl py-1 z-50"
                  style={{
                    border: "1px solid #E5E5E5",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                  }}
                  data-testid="header-avatar-menu"
                >
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      navigate("/me");
                    }}
                    className="flex items-center gap-2 w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50"
                    data-testid="my-profile-link"
                  >
                    <User size={14} /> Mon profil
                  </button>
                  <div className="my-1 border-t" style={{ borderColor: "#E5E5E5" }} />
                  <button
                    onClick={logout}
                    className="flex items-center gap-2 w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50"
                    style={{ color: "#EF4444" }}
                    data-testid="logout-button"
                  >
                    <LogOut size={14} /> Déconnexion
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => setOpen(!open)}
              className="md:hidden p-2"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {open && (
          <div
            className="md:hidden border-t"
            style={{ borderColor: "#E5E5E5" }}
            data-testid="mobile-menu"
          >
            <div className="px-4 py-2 flex flex-col gap-1">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 px-3 py-3 rounded-lg text-sm font-medium hover:bg-gray-50"
                >
                  <n.icon size={16} />
                  <span>{n.label}</span>
                </NavLink>
              ))}
              <NavLink
                to="/me"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 px-3 py-3 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                <User size={16} /> Mon profil
              </NavLink>
              <button
                onClick={logout}
                className="flex items-center gap-2 px-3 py-3 text-sm font-medium"
                style={{ color: "#EF4444" }}
              >
                <LogOut size={16} /> Déconnexion
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="max-w-[1024px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {children}
      </main>
    </div>
  );
}
